import React, { useState, useEffect, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { qrCodeService, templateService } from '../services/api';
import NotificationModal from './NotificationModal';
import ConfirmModal from './ConfirmModal';
import './AdvancedQRCodeGenerator.css';

const AdvancedQRCodeGenerator = ({ url, onClose, qrCodeName, qrCodeId, qrCodeDestino, initialCustomization }) => {
  const [qrCode, setQrCode] = useState(null);
  const [options, setOptions] = useState({
    width: 1024,
    height: 1024,
    type: 'canvas',
    data: url,
    image: '',
    margin: 0,
    qrOptions: {
      typeNumber: 0,
      mode: 'Byte',
      errorCorrectionLevel: 'H'
    },
    imageOptions: {
      hideBackgroundDots: true,
      imageSize: 0.4,
      margin: 0,
      crossOrigin: 'anonymous'
    },
    dotsOptions: {
      color: '#000000',
      type: 'rounded'
    },
    backgroundOptions: {
      color: '#ffffff'
    },
    cornersSquareOptions: {
      color: '#000000',
      type: 'extra-rounded'
    },
    cornersDotOptions: {
      color: '#000000',
      type: 'dot'
    }
  });

  const [logoFile, setLogoFile] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customWidth, setCustomWidth] = useState(1024);
  const [customHeight, setCustomHeight] = useState(1024);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showTemplatesSubmenu, setShowTemplatesSubmenu] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const qrCodeRef = useRef(null);

  useEffect(() => {
    loadTemplates();

    // Inicializar QRCode com customização se houver
    let initialOptions = { ...options, data: url };

    if (initialCustomization) {
      initialOptions = {
        ...initialOptions,
        ...initialCustomization,
        data: url // Manter a URL atual
      };
      setCustomWidth(initialCustomization.width || 1024);
      setCustomHeight(initialCustomization.height || 1024);
      if (initialCustomization.image) {
        setLogoFile(initialCustomization.image);
      }
      setOptions(initialOptions);
    }

    const qr = new QRCodeStyling(initialOptions);
    setQrCode(qr);
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await templateService.list();
      setTemplates(data);
    } catch (err) {
      console.error('Erro ao carregar templates:', err);
      setTemplates([]);
    }
  };

  const applyCustomization = (customization) => {
    const newOptions = {
      ...customization,
      data: url // Manter a URL atual
    };
    setOptions(newOptions);
    setCustomWidth(customization.width || 1024);
    setCustomHeight(customization.height || 1024);
    if (customization.image) {
      setLogoFile(customization.image);
    }
  };

  useEffect(() => {
    if (qrCode && qrCodeRef.current) {
      qrCodeRef.current.innerHTML = '';
      qrCode.append(qrCodeRef.current);
    }
  }, [qrCode]);

  useEffect(() => {
    if (qrCode) {
      // Para o preview no modal, mantemos a margem do usuário
      qrCode.update(options);
    }
  }, [options, qrCode]);

  const handleOptionChange = (category, key, value) => {
    setOptions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoFile(event.target.result);
        setOptions(prev => ({
          ...prev,
          image: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setOptions(prev => ({
      ...prev,
      image: ''
    }));
  };

  const handleDownload = (extension) => {
    if (qrCode) {
      qrCode.download({
        name: qrCodeName || 'qrcode',
        extension: extension
      });
    }
  };

  const handleSaveCustomization = async () => {
    if (!qrCodeId) {
      setNotification({
        message: 'QR Code precisa ser criado primeiro antes de salvar a personalização',
        type: 'warning'
      });
      return;
    }

    try {
      setSaving(true);
      const customization = {
        width: options.width,
        height: options.height,
        margin: options.margin,
        qrOptions: options.qrOptions,
        imageOptions: options.imageOptions,
        dotsOptions: options.dotsOptions,
        backgroundOptions: options.backgroundOptions,
        cornersSquareOptions: options.cornersSquareOptions,
        cornersDotOptions: options.cornersDotOptions,
        image: options.image
      };

      await qrCodeService.update({
        id: qrCodeId,
        customizacao: customization,
        urlDestino: qrCodeDestino,
        nome: qrCodeName
      });

      setNotification({
        message: 'Personalização salva com sucesso!',
        type: 'success'
      });
    } catch (err) {
      console.error('Erro ao salvar personalização:', err);
      setNotification({
        message: 'Erro ao salvar personalização: ' + (err.response?.data?.error || err.message),
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      setNotification({
        message: 'Digite um nome para o template',
        type: 'warning'
      });
      return;
    }

    try {
      setSaving(true);
      const customization = {
        width: options.width,
        height: options.height,
        margin: options.margin,
        qrOptions: options.qrOptions,
        imageOptions: options.imageOptions,
        dotsOptions: options.dotsOptions,
        backgroundOptions: options.backgroundOptions,
        cornersSquareOptions: options.cornersSquareOptions,
        cornersDotOptions: options.cornersDotOptions,
        image: options.image
      };

      await templateService.create({
        nome: templateName,
        customizacao: customization
      });

      setNotification({
        message: 'Template salvo com sucesso!',
        type: 'success'
      });
      setTemplateName('');
      setShowSaveTemplate(false);
      await loadTemplates();
    } catch (err) {
      console.error('Erro ao salvar template:', err);
      setNotification({
        message: 'Erro ao salvar template',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = (template) => {
    applyCustomization(template.customizacao);
  };

  const handleDeleteTemplate = async (templateId) => {
    setConfirmDialog({
      message: 'Deseja deletar este template?',
      onConfirm: async () => {
        try {
          await templateService.delete(templateId);
          await loadTemplates();
          setNotification({
            message: 'Template deletado com sucesso!',
            type: 'success'
          });
        } catch (err) {
          console.error('Erro ao deletar template:', err);
          setNotification({
            message: 'Erro ao deletar template',
            type: 'error'
          });
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const dotStyles = ['rounded', 'dots', 'classy', 'classy-rounded', 'square', 'extra-rounded'];
  const cornerSquareStyles = ['dot', 'square', 'extra-rounded'];
  const cornerDotStyles = ['dot', 'square'];

  return (
    <div className="qr-modal-overlay" onClick={onClose}>
      <div className="qr-modal-content advanced" onClick={(e) => e.stopPropagation()}>
        <div className="qr-modal-header">
          <div className="header-left">
            <div className="settings-menu-container">
              <button
                className="settings-button"
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                title="Configurações"
              >
                ⚙️
              </button>

              {showSettingsMenu && (
                <div className="settings-dropdown">
                  {qrCodeId && (
                    <button
                      onClick={() => {
                        handleSaveCustomization();
                        setShowSettingsMenu(false);
                      }}
                      className="settings-menu-item"
                      disabled={saving}
                    >
                      💾 Salvar no QR Code
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setShowTemplatesSubmenu(!showTemplatesSubmenu);
                    }}
                    className="settings-menu-item"
                  >
                    ⭐ Templates {showTemplatesSubmenu ? '▼' : '▶'}
                  </button>

                  {showTemplatesSubmenu && (
                    <div className="templates-submenu">
                      <div className="submenu-section">
                        <button
                          onClick={() => setShowSaveTemplate(!showSaveTemplate)}
                          className="submenu-item"
                        >
                          💾 Salvar como Template
                        </button>

                        {showSaveTemplate && (
                          <div className="save-template-inline">
                            <input
                              type="text"
                              placeholder="Nome do template"
                              value={templateName}
                              onChange={(e) => setTemplateName(e.target.value)}
                              className="template-name-input-inline"
                            />
                            <button
                              onClick={() => {
                                handleSaveAsTemplate();
                                setShowSettingsMenu(false);
                              }}
                              className="btn-confirm-inline"
                              disabled={saving}
                            >
                              ✓
                            </button>
                          </div>
                        )}
                      </div>

                      {templates.length > 0 && (
                        <div className="submenu-section">
                          <div className="submenu-label">Carregar Template:</div>
                          {templates.map(template => (
                            <div key={template.id} className="template-item-inline">
                              <span
                                className="template-name-clickable"
                                onClick={() => {
                                  handleApplyTemplate(template);
                                  setShowSettingsMenu(false);
                                  setShowTemplatesSubmenu(false);
                                }}
                              >
                                {template.nome}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTemplate(template.id);
                                }}
                                className="btn-delete-inline"
                                title="Deletar"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <h2>Personalizar QR Code</h2>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="qr-generator-container">
          {/* Preview */}
          <div className="qr-preview-section">
            <div className="qr-preview-box">
              <div ref={qrCodeRef} className="qr-code-canvas"></div>
            </div>

            <div className="qr-download-section">
              <h3>Download</h3>
              <div className="download-buttons">
                <button onClick={() => handleDownload('png')} className="btn-download">
                  PNG
                </button>
                <button onClick={() => handleDownload('jpeg')} className="btn-download">
                  JPEG
                </button>
                <button onClick={() => handleDownload('svg')} className="btn-download">
                  SVG
                </button>
                <button onClick={() => handleDownload('webp')} className="btn-download">
                  WEBP
                </button>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="qr-controls-section">
            <div className="controls-scroll">
              {/* Configurações Básicas */}
              <div className="control-group">
                <h3>Configurações Básicas</h3>

                <div className="control-item">
                  <label>Largura: {customWidth}px</label>
                  <input
                    type="range"
                    min="256"
                    max="4096"
                    step="128"
                    value={customWidth}
                    onChange={(e) => {
                      const width = parseInt(e.target.value);
                      setCustomWidth(width);
                      setOptions(prev => ({ ...prev, width }));
                    }}
                  />
                  <input
                    type="number"
                    min="256"
                    max="4096"
                    value={customWidth}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Permitir campo vazio ou qualquer número durante digitação
                      setCustomWidth(value);
                    }}
                    onBlur={(e) => {
                      const width = parseInt(e.target.value);
                      if (isNaN(width) || width < 256) {
                        setCustomWidth(1024);
                        setOptions(prev => ({ ...prev, width: 1024 }));
                      } else {
                        const validWidth = Math.min(Math.max(width, 256), 4096);
                        setCustomWidth(validWidth);
                        setOptions(prev => ({ ...prev, width: validWidth }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    className="resolution-input"
                  />
                </div>

                <div className="control-item">
                  <label>Altura: {customHeight}px</label>
                  <input
                    type="range"
                    min="256"
                    max="4096"
                    step="128"
                    value={customHeight}
                    onChange={(e) => {
                      const height = parseInt(e.target.value);
                      setCustomHeight(height);
                      setOptions(prev => ({ ...prev, height }));
                    }}
                  />
                  <input
                    type="number"
                    min="256"
                    max="4096"
                    value={customHeight}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Permitir campo vazio ou qualquer número durante digitação
                      setCustomHeight(value);
                    }}
                    onBlur={(e) => {
                      const height = parseInt(e.target.value);
                      if (isNaN(height) || height < 256) {
                        setCustomHeight(1024);
                        setOptions(prev => ({ ...prev, height: 1024 }));
                      } else {
                        const validHeight = Math.min(Math.max(height, 256), 4096);
                        setCustomHeight(validHeight);
                        setOptions(prev => ({ ...prev, height: validHeight }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    className="resolution-input"
                  />
                </div>

                <div className="control-item">
                  <label>Margem do QR Code: {options.margin}px</label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={options.margin}
                    onChange={(e) => {
                      const margin = parseInt(e.target.value);
                      setOptions(prev => ({ ...prev, margin }));
                    }}
                  />
                </div>

                <div className="control-item">
                  <label>Correção de Erro:</label>
                  <select
                    value={options.qrOptions.errorCorrectionLevel}
                    onChange={(e) => handleOptionChange('qrOptions', 'errorCorrectionLevel', e.target.value)}
                  >
                    <option value="L">Baixa (7%)</option>
                    <option value="M">Média (15%)</option>
                    <option value="Q">Alta (25%)</option>
                    <option value="H">Muito Alta (30%)</option>
                  </select>
                </div>
              </div>

              {/* Logo Central */}
              <div className="control-group">
                <h3>Logo Central</h3>

                <div className="control-item">
                  <label>Upload Logo:</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="file-input"
                  />
                  {logoFile && (
                    <button onClick={handleRemoveLogo} className="btn-remove-logo">
                      Remover Logo
                    </button>
                  )}
                </div>

                {logoFile && (
                  <>
                    <div className="control-item">
                      <label>Tamanho da Logo: {(options.imageOptions.imageSize * 100).toFixed(0)}%</label>
                      <input
                        type="range"
                        min="0.1"
                        max="0.6"
                        step="0.05"
                        value={options.imageOptions.imageSize}
                        onChange={(e) => {
                          const imageSize = parseFloat(e.target.value);
                          setOptions(prev => ({
                            ...prev,
                            imageOptions: {
                              ...prev.imageOptions,
                              imageSize
                            }
                          }));
                        }}
                      />
                    </div>

                    <div className="control-item">
                      <label>Margem da Logo: {options.imageOptions.margin}px</label>
                      <input
                        type="range"
                        min="0"
                        max="30"
                        value={options.imageOptions.margin}
                        onChange={(e) => {
                          const margin = parseInt(e.target.value);
                          setOptions(prev => ({
                            ...prev,
                            imageOptions: {
                              ...prev.imageOptions,
                              margin
                            }
                          }));
                        }}
                      />
                    </div>

                    <div className="control-item checkbox">
                      <input
                        type="checkbox"
                        id="hideBackgroundDots"
                        checked={options.imageOptions.hideBackgroundDots}
                        onChange={(e) => handleOptionChange('imageOptions', 'hideBackgroundDots', e.target.checked)}
                      />
                      <label htmlFor="hideBackgroundDots">Ocultar pontos atrás da logo</label>
                    </div>
                  </>
                )}
              </div>

              {/* Cores */}
              <div className="control-group">
                <h3>Cores</h3>

                <div className="control-item">
                  <label>Cor dos Pontos:</label>
                  <input
                    type="color"
                    value={options.dotsOptions.color}
                    onChange={(e) => handleOptionChange('dotsOptions', 'color', e.target.value)}
                  />
                  <input
                    type="text"
                    value={options.dotsOptions.color}
                    onChange={(e) => handleOptionChange('dotsOptions', 'color', e.target.value)}
                    className="color-text"
                  />
                </div>

                <div className="control-item">
                  <label>Cor de Fundo:</label>
                  <input
                    type="color"
                    value={options.backgroundOptions.color}
                    onChange={(e) => handleOptionChange('backgroundOptions', 'color', e.target.value)}
                  />
                  <input
                    type="text"
                    value={options.backgroundOptions.color}
                    onChange={(e) => handleOptionChange('backgroundOptions', 'color', e.target.value)}
                    className="color-text"
                  />
                </div>

                <div className="control-item">
                  <label>Cor dos Cantos Externos:</label>
                  <input
                    type="color"
                    value={options.cornersSquareOptions.color}
                    onChange={(e) => handleOptionChange('cornersSquareOptions', 'color', e.target.value)}
                  />
                  <input
                    type="text"
                    value={options.cornersSquareOptions.color}
                    onChange={(e) => handleOptionChange('cornersSquareOptions', 'color', e.target.value)}
                    className="color-text"
                  />
                </div>

                <div className="control-item">
                  <label>Cor dos Cantos Internos:</label>
                  <input
                    type="color"
                    value={options.cornersDotOptions.color}
                    onChange={(e) => handleOptionChange('cornersDotOptions', 'color', e.target.value)}
                  />
                  <input
                    type="text"
                    value={options.cornersDotOptions.color}
                    onChange={(e) => handleOptionChange('cornersDotOptions', 'color', e.target.value)}
                    className="color-text"
                  />
                </div>
              </div>

              {/* Estilos Avançados */}
              <div className="control-group">
                <button
                  className="toggle-advanced"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  {showAdvanced ? '▼' : '►'} Estilos Avançados
                </button>

                {showAdvanced && (
                  <>
                    <div className="control-item">
                      <label>Estilo dos Pontos:</label>
                      <div className="style-grid">
                        {dotStyles.map(style => (
                          <button
                            key={style}
                            className={`style-button ${options.dotsOptions.type === style ? 'active' : ''}`}
                            onClick={() => handleOptionChange('dotsOptions', 'type', style)}
                          >
                            {style}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="control-item">
                      <label>Estilo dos Cantos Externos:</label>
                      <div className="style-grid">
                        {cornerSquareStyles.map(style => (
                          <button
                            key={style}
                            className={`style-button ${options.cornersSquareOptions.type === style ? 'active' : ''}`}
                            onClick={() => handleOptionChange('cornersSquareOptions', 'type', style)}
                          >
                            {style}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="control-item">
                      <label>Estilo dos Cantos Internos:</label>
                      <div className="style-grid">
                        {cornerDotStyles.map(style => (
                          <button
                            key={style}
                            className={`style-button ${options.cornersDotOptions.type === style ? 'active' : ''}`}
                            onClick={() => handleOptionChange('cornersDotOptions', 'type', style)}
                          >
                            {style}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {notification && (
        <NotificationModal
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {confirmDialog && (
        <ConfirmModal
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
    </div>
  );
};

export default AdvancedQRCodeGenerator;